import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, ChevronRight, ChevronLeft, Trash2, Download, Clock, CheckCircle, XCircle, AlertTriangle, Moon, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

const LOG_TYPES = {
  all: { label: 'הכל', color: 'gray' },
  message_received: { label: 'הודעה התקבלה', color: 'blue', icon: '📨' },
  message_approved: { label: 'הודעה אושרה', color: 'green', icon: '✅' },
  message_rejected: { label: 'הודעה נדחתה', color: 'red', icon: '❌' },
  sent_to_general: { label: 'נשלח לכללי', color: 'blue', icon: '📡' },
  sent_to_phone: { label: 'נשלח לטלפון', color: 'green', icon: '📞' },
  sent_to_personal: { label: 'נשלח לאישי', color: 'purple', icon: '👤' },
  duplicate_detected: { label: 'כפילות', color: 'yellow', icon: '🔄' },
  shabbat_blocked: { label: 'נחסם בשבת', color: 'purple', icon: '🕯️' },
  news_summary: { label: 'סיכום חדשות', color: 'indigo', icon: '📰' },
  daily_deal: { label: 'מבצע היום', color: 'pink', icon: '🎁' },
  breaking_news: { label: 'חדשות חריגות', color: 'orange', icon: '🚨' },
  error: { label: 'שגיאה', color: 'red', icon: '💥' },
  system: { label: 'מערכת', color: 'gray', icon: '⚙️' }
};

const MAX_WINDOW = 5000;
const PAGE_SIZES = [50, 100, 200, 500, 1000];

const STATUS_COLORS = {
  success: 'bg-green-100 text-green-800 border-green-200',
  error: 'bg-red-100 text-red-800 border-red-200',
  warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  info: 'bg-blue-100 text-blue-800 border-blue-200',
  blocked: 'bg-purple-100 text-purple-800 border-purple-200'
};

export default function LogTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filter, setFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('');
  const [channels, setChannels] = useState([]);
  const [expandedLog, setExpandedLog] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [total, setTotal] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    setPage(1);
  }, [filter, channelFilter, pageSize]);

  useEffect(() => {
    loadLogs();
  }, [filter, channelFilter, page, pageSize]);

  useEffect(() => {
    loadChannels();
  }, []);

  useEffect(() => {
    // רענון אוטומטי רק בעמוד הראשון, כדי שהרשימה לא "תקפוץ" בזמן דפדוף
    if (autoRefresh && page === 1) {
      intervalRef.current = setInterval(loadLogs, 5000); // רענון כל 5 שניות
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, filter, channelFilter, page, pageSize]);

  const loadLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('type', filter);
      if (channelFilter) params.append('channel', channelFilter);
      params.append('limit', String(pageSize));
      params.append('offset', String((page - 1) * pageSize));

      const response = await api.get(`/logs?${params.toString()}`);
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('שגיאה בטעינת לוגים:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChannels = async () => {
    try {
      const response = await api.get('/channels');
      setChannels(response.data || []);
    } catch (error) {
      console.error('שגיאה בטעינת ערוצים');
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('האם למחוק את כל הלוגים? פעולה זו בלתי הפיכה!')) {
      return;
    }

    try {
      await api.delete('/logs/clear');
      toast.success('הלוגים נמחקו');
      loadLogs();
    } catch (error) {
      toast.error('שגיאה במחיקת לוגים');
    }
  };

  const handleExportLogs = async () => {
    let exportLogs = logs;
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('type', filter);
      if (channelFilter) params.append('channel', channelFilter);
      params.append('limit', String(MAX_WINDOW));
      const response = await api.get(`/logs?${params.toString()}`);
      exportLogs = response.data.logs || logs;
    } catch (error) {
      toast.error('שגיאה בשליפת הלוגים לייצוא - מיוצא העמוד הנוכחי בלבד');
    }
    const csvContent = [
      ['זמן', 'סוג', 'ערוץ', 'פעולה', 'סטטוס', 'פרטים'].join(','),
      ...exportLogs.map(log => [
        formatDate(log.timestamp),
        LOG_TYPES[log.log_type]?.label || log.log_type,
        log.channel_name || log.channel_username || '',
        log.action,
        log.status,
        (log.details || '').replace(/,/g, ';').replace(/\n/g, ' ')
      ].join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `activity_log_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('הלוג יוצא בהצלחה');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString.includes('T') ? dateString : dateString.replace(' ', 'T') + 'Z');
      return date.toLocaleString('he-IL', {
        timeZone: 'Asia/Jerusalem',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'error': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'blocked': return <Moon className="w-4 h-4 text-purple-600" />;
      default: return <Clock className="w-4 h-4 text-blue-600" />;
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('הועתק!', { duration: 1000 });
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const pager = (
    <div className="bg-white rounded-lg shadow p-3 flex flex-wrap items-center justify-between gap-3 text-sm">
      <span className="text-gray-600">
        מציג <strong>{from}-{to}</strong> מתוך <strong>{total}</strong>
        {total >= MAX_WINDOW && ` (החלון מוגבל ל-${MAX_WINDOW} האחרונות)`}
      </span>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setPage(1)}
          disabled={page <= 1}
          className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
        >ראשון</button>
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          className="flex items-center px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
        ><ChevronRight className="w-4 h-4" />הקודם</button>
        <span>
          עמוד{' '}
          <input
            type="number"
            min={1}
            max={totalPages}
            value={page}
            onChange={(e) => {
              const n = parseInt(e.target.value);
              if (n) setPage(Math.min(Math.max(n, 1), totalPages));
            }}
            className="w-16 px-2 py-1 border border-gray-300 rounded text-center"
          />{' '}
          מתוך {totalPages}
        </span>
        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          className="flex items-center px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
        >הבא<ChevronLeft className="w-4 h-4" /></button>
        <button
          onClick={() => setPage(totalPages)}
          disabled={page >= totalPages}
          className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
        >אחרון</button>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(parseInt(e.target.value))}
          className="px-2 py-1 border border-gray-300 rounded"
          title="הודעות בעמוד"
        >
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>{n} בעמוד</option>
          ))}
        </select>
      </div>
    </div>
  );

  if (loading && logs.length === 0) {
    return <div className="text-center py-12">טוען לוגים...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header & Controls */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-900">📋 לוג פעילות</h2>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Auto Refresh Toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">רענון אוטומטי</span>
            </label>

            {/* Filter by Type */}
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(LOG_TYPES).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>

            {/* Filter by Channel */}
            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">כל הערוצים</option>
              {channels.map((ch) => (
                <option key={ch.id} value={ch.username}>{ch.name}</option>
              ))}
            </select>

            {/* Actions */}
            <button
              onClick={loadLogs}
              className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <RefreshCw className="w-4 h-4 ml-1" />
              רענן
            </button>
            
            <button
              onClick={handleExportLogs}
              className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
            >
              <Download className="w-4 h-4 ml-1" />
              ייצא
            </button>
            
            <button
              onClick={handleClearLogs}
              className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
            >
              <Trash2 className="w-4 h-4 ml-1" />
              נקה
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap gap-4 mt-4 text-sm">
          <span className="text-gray-600">סה"כ בלוג: <strong>{total}</strong></span>
          <span className="text-gray-400">| בעמוד זה:</span>
          <span className="text-green-600">הצלחות: <strong>{logs.filter(l => l.status === 'success').length}</strong></span>
          <span className="text-red-600">שגיאות: <strong>{logs.filter(l => l.status === 'error').length}</strong></span>
          <span className="text-purple-600">נחסמו: <strong>{logs.filter(l => l.status === 'blocked').length}</strong></span>
          <span className="text-yellow-600">כפילויות: <strong>{logs.filter(l => l.is_duplicate).length}</strong></span>
        </div>
      </div>

      {pager}

      {/* Logs List */}
      <div className="space-y-2">
        {logs.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            אין לוגים להצגה
          </div>
        ) : (
          logs.map((log) => {
            const logType = LOG_TYPES[log.log_type] || { label: log.log_type, color: 'gray', icon: '📝' };
            const isExpanded = expandedLog === log.id;
            
            return (
              <div
                key={log.id}
                className={`bg-white rounded-lg shadow border-r-4 ${STATUS_COLORS[log.status] || 'border-gray-200'} cursor-pointer hover:shadow-md transition-shadow`}
                onClick={() => setExpandedLog(isExpanded ? null : log.id)}
              >
                {/* Main Row */}
                <div className="p-3 flex items-center gap-3">
                  {/* Status Icon */}
                  <div className="flex-shrink-0">
                    {getStatusIcon(log.status)}
                  </div>

                  {/* Time */}
                  <div className="text-xs text-gray-500 w-28 flex-shrink-0">
                    {formatDate(log.timestamp)}
                  </div>

                  {/* Type Badge */}
                  <div className={`px-2 py-0.5 rounded text-xs font-medium bg-${logType.color}-100 text-${logType.color}-800 flex-shrink-0`}>
                    {logType.icon} {logType.label}
                  </div>

                  {/* Channel */}
                  {(log.channel_name || log.channel_username) && (
                    <div className="text-sm font-medium text-gray-700 flex-shrink-0">
                      📢 {log.channel_name || `@${log.channel_username}`}
                    </div>
                  )}

                  {/* Action */}
                  <div className="flex-1 text-sm text-gray-800 truncate">
                    {log.action}
                  </div>

                  {/* Target */}
                  {log.target && (
                    <div className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-600 flex-shrink-0">
                      → {log.target}
                    </div>
                  )}

                  {/* Badges */}
                  <div className="flex gap-1 flex-shrink-0">
                    {log.is_shabbat && (
                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">🕯️</span>
                    )}
                    {log.is_duplicate && (
                      <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">🔄</span>
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-3">
                    {log.details && (
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium text-gray-500">פרטים:</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); copyToClipboard(log.details); }}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            <Copy className="w-3 h-3 inline ml-1" />
                            העתק
                          </button>
                        </div>
                        <div className="text-sm bg-white p-2 rounded border border-gray-200 whitespace-pre-wrap max-h-40 overflow-auto">
                          {log.details}
                        </div>
                      </div>
                    )}

                    {log.gemini_response && (
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium text-gray-500">תשובת Gemini:</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); copyToClipboard(log.gemini_response); }}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            <Copy className="w-3 h-3 inline ml-1" />
                            העתק
                          </button>
                        </div>
                        <div className="text-sm bg-blue-50 p-2 rounded border border-blue-200 whitespace-pre-wrap max-h-40 overflow-auto">
                          {log.gemini_response}
                        </div>
                      </div>
                    )}

                    {log.rejection_reason && (
                      <div>
                        <span className="text-xs font-medium text-gray-500">סיבת דחייה:</span>
                        <div className="text-sm bg-red-50 p-2 rounded border border-red-200 mt-1">
                          {log.rejection_reason}
                        </div>
                      </div>
                    )}

                    {log.error_message && (
                      <div>
                        <span className="text-xs font-medium text-gray-500">שגיאה:</span>
                        <div className="text-sm bg-red-50 p-2 rounded border border-red-200 mt-1 text-red-700">
                          {log.error_message}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-4 text-xs text-gray-500">
                      {log.message_id && <span>מזהה הודעה: {log.message_id}</span>}
                      <span>ID: {log.id}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {logs.length > 0 && pager}
    </div>
  );
}
